/**
 * Übersicht-Tab der Buchhaltungsseite
 */

import {
  CreditCard, Plus, Upload, FileText, TrendingUp, TrendingDown,
  Scale, Receipt, Users, Link2, FileSpreadsheet, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function UebersichtTab({
  konten, umsaetze, belege, verbindlichkeiten, glaeubiger,
  bankverbindungen, erinnerungen, kategorien,
  einnahmenSumme, ausgabenSumme, summeVerbindlichkeiten, belegeOhneZuordnung,
  openChildTab, handleRechnungslegung, selectedKlient
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Konten</p>
            <p className="text-2xl font-bold">{konten.length}</p>
            <p className="text-xs text-slate-400 mt-1">
              {bankverbindungen.length > 0 && `${bankverbindungen.length} mit Online-Banking`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Umsätze</p>
            <p className="text-2xl font-bold">{umsaetze.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-green-600">Einnahmen</p>
            <p className="text-2xl font-bold text-green-700">
              {einnahmenSumme.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-red-600">Ausgaben</p>
            <p className="text-2xl font-bold text-red-700">
              {ausgabenSumme.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={summeVerbindlichkeiten > 0 ? 'border-l-4 border-l-amber-500' : ''}>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Scale className="w-4 h-4" /> Verbindlichkeiten
            </p>
            <p className="text-2xl font-bold text-amber-700">
              {summeVerbindlichkeiten.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </p>
            <p className="text-xs text-slate-400 mt-1">{verbindlichkeiten.length} offene Positionen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Receipt className="w-4 h-4" /> Belege
            </p>
            <p className="text-2xl font-bold">{belege.length}</p>
            {belegeOhneZuordnung > 0 && (
              <p className="text-xs text-amber-600 mt-1">{belegeOhneZuordnung} ohne Zuordnung</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Users className="w-4 h-4" /> Gläubiger
            </p>
            <p className="text-2xl font-bold">{glaeubiger.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Nettovermögen</p>
            <p className={`text-2xl font-bold ${(konten.reduce((s, k) => s + (k.kontostand || 0), 0) - summeVerbindlichkeiten) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {(konten.reduce((s, k) => s + (k.kontostand || 0), 0) - summeVerbindlichkeiten).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Button onClick={() => openChildTab('/schnellerfassung/konto', { title: 'Neues Konto', forceNew: true, params: { klientId: selectedKlient } })} data-testid="btn-konto-anlegen">
          <Plus className="w-4 h-4 mr-2" /> Konto anlegen
        </Button>
        <Button variant="outline" onClick={() => openChildTab('/schnellerfassung/csv-import', { title: 'CSV/MT940 Import', forceNew: true, params: { klientId: selectedKlient, konten } })} data-testid="btn-import">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV/MT940 Import
        </Button>
        <Button variant="outline" onClick={() => openChildTab('/schnellerfassung/kontoauszug', { title: 'Kontoauszug Upload', forceNew: true, params: { klientId: selectedKlient, konten } })}>
          <Upload className="w-4 h-4 mr-2" /> Kontoauszug (PDF)
        </Button>
        <Button variant="outline" onClick={handleRechnungslegung}>
          <FileText className="w-4 h-4 mr-2" /> Rechnungslegung
        </Button>
      </div>

      {umsaetze.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Letzte Transaktionen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {umsaetze.slice(0, 5).map(u => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.betrag >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      {u.betrag >= 0 ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{u.verwendungszweck || u.empfaenger_auftraggeber || '-'}</p>
                      <p className="text-xs text-slate-400">{u.datum ? new Date(u.datum).toLocaleDateString('de-DE') : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-medium ${u.betrag >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {u.betrag >= 0 ? '+' : ''}{u.betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                    {u.kategorie && (
                      <Badge variant="outline" className="text-xs mt-1">
                        {kategorien.find(k => k.id === u.kategorie)?.name || u.kategorie}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
